import { useMemo, Children, forwardRef, useCallback, ReactNode } from 'react';
import classNames from 'classnames';
// import { Box, Space, Stack } from '@fastlooptech/layout-components';
import { Body, Label } from '@/client/foundations/Typography';
import { spaceUnit } from '@/helpers/utils.helper';
import { Icons } from '@/components/Icons';
import { Box, Space, Stack } from '@/layouts/LayoutComponents';
import { InputsNS } from '@/types/inputs';

export type InputWrapProps = {
  id?: string;
  name?: string;
  'data-test-id'?: string;
  /**
   * The label input.
   * @type string
   */
  label?: string;
  /** css class of Input Field content type */
  lableClassName?: string;
  /** label color */
  labelColor?: string;
  /** label size */
  labelSize?: string;
  /** label style */
  labelStyle?: any;
  /**
   * The assistive text input.
   * @type [InputsNS.Assistive] | [{ type?: "informative", message: string }, InputsNS.Assistive];
   * @default {message:"",type:"informative"}
   * @optional
   */
  assistive?: InputsNS.AssistiveType;
  /**
   * The boolean that indicate if the input is required.
   * @type boolean
   * @default false
   * @optional
   */
  required?: boolean;
  /**
   * The boolean that indicate if the input is disabled.
   * @type boolean
   * @default false
   * @optional
   */
  disabled?: boolean;
  /**
   * The width of the input.
   * @type string | number
   * @default "100%"
   * @optional
   */
  width?: number | string;
  /**
   * The html of the label input.
   * @type string
   */
  htmlFor?: string;
  /**
   * The className of the input.
   * @type string
   * @optional
   */
  className?: string;
  /**
   * The className of the label.
   * @type string
   * @optional
   */
  classNameLabel?: string;
  // children: BoxChildren | BoxChildren[];
  children: ReactNode,
  actionChildren?: ReactNode;
};


const InputWrap = forwardRef<HTMLElement, InputWrapProps>(
  ({ width = '100%', assistive, children, label, lableClassName="", labelColor="", labelSize="", labelStyle={}, ...props }, ref) => {
    const assistiveIcon = useCallback((type: InputsNS.StatusType | InputsNS.StatusType.Informative) => {
      const assistiveIcons: {
        [k in InputsNS.StatusType]: keyof typeof Icons;
      } = {
        [InputsNS.StatusType.Disabled]: 'Warning',
        [InputsNS.StatusType.Informative]: 'Warning',
        [InputsNS.StatusType.Warning]: 'Warning',
        [InputsNS.StatusType.Error]: 'Error',
        [InputsNS.StatusType.Success]: 'Success',
      };

      return Icons[assistiveIcons[type]]({
        id: `${type}-icon`,
        size: 's',
        className: classNames('w-2', {
          'fill-interactive-black': type === InputsNS.StatusType.Informative,
          'fill-feedback-negative': type === InputsNS.StatusType.Error,
          'fill-feedback-warning-500': type === InputsNS.StatusType.Warning,
          'fill-feedback-success-500': type === InputsNS.StatusType.Success,
        }),
      });
    }, []);

    const inputLabel = useMemo(() => {
      if (label)
        return (
          <Box vAlignContent="center" width="100%" style={{lineHeight: '100%'}}>
            {/* <Label
              className={classNames('!text-input-default-label', props.classNameLabel)}
              name={props?.name}
              id={props?.id}
              htmlFor={props?.htmlFor || props?.id}
            >
              {`${label} ${props.required ? '*' : ''}`}
            </Label> */}
            <Label
              htmlFor={props?.htmlFor || props?.id}
              className={classNames(lableClassName || '!text-input-default-label', labelSize)}
              style={{color: labelColor, paddingLeft: "0px"}}
              // required={props?.required}
              disabled={props?.disabled}
            >
              {label ? `${label}${props.required ? '*' : ''}` : ""}
            </Label>
            <Space fluid />
            {props.actionChildren}
          </Box>
        );
    }, [label, props.actionChildren, props.disabled, props.required, props.classNameLabel]);

    const assistiveMessage = useMemo(() => {
      const assistiveSorted = assistive?.sort((a, _) => (a.type === InputsNS.StatusType.Informative ? -1 : 0));
      return assistiveSorted?.map((item, index) => {
        return (
          <Stack
            data-test-id={props['data-test-id'] + '-assistive'}
            data-test-type={item.type}
            key={`InputWrap--${index}`}
            gap={spaceUnit * 0.5}
            vAlignContent="center"
          >
            <Box basis={spaceUnit * 2}>{assistiveIcon(item.type ? item.type : InputsNS.StatusType.Informative)}</Box>
            <Body
              component="label"
              size="xxs"
              bold
              className={classNames({
                'text-interactive-black': item.type === InputsNS.StatusType.Informative,
                'text-feedback-negative': item.type === InputsNS.StatusType.Error,
                'text-feedback-warning-500': item.type === InputsNS.StatusType.Warning,
                'text-feedback-success-500': item.type === InputsNS.StatusType.Success,
              })}
            >
              {item.message}
            </Body>
          </Stack>
        );
      });
    }, [assistive, assistiveIcon, props]);

    const wrappedChildren = useMemo(() => {
      if (Children.count(children) <= 1)
        return (
          <Box width={width} className={props.className}>
            {/* {children as BoxChildren} */}
            {children as ReactNode}
          </Box>
        );
      return (
        <Stack gap={spaceUnit} width={width} column className={props.className}>
          {/* {children as BoxChildren} */}
          {children as ReactNode}
        </Stack>
      );
    }, [children, props.className, width]);

    return (
      <Stack
        width={width}
        column
        component="fieldset"
        className='gap-input-label-gap'
        data-test-id={props['data-test-id']}
        ref={ref}
        disabled={props.disabled}
      >
        {inputLabel}
        {wrappedChildren}
        {!!assistive && assistiveMessage}
      </Stack>
    );
  }
);

InputWrap.displayName = 'InputWrap';
export { InputWrap };
